import { friendlyDbError } from '../utils/friendlyDbError';
import { create } from 'zustand';
import { supabase } from '../supabase';
//import { terminal } from 'virtual:terminal';

interface OtpStore {
  loading: boolean;
  error: string | null;
  sendOtp: (phone: string) => Promise<boolean>;
  verifyOtp: (phone: string, code: string) => Promise<boolean>;
}

export const useOtpStore = create<OtpStore>((set) => ({
  loading: false,
  error: null,

  sendOtp: async (phone: string) => {
    set({ loading: true, error: null });
    try {
      terminal.log('📱 Sending OTP to:', phone);

      // Get Twilio configuration
      const { data: config, error: configError } = await supabase
        .from('api_configurations')
        .select('*')
        .eq('service_name', 'twilio')
        .single();

      if (configError) {
        terminal.error('❌ No Twilio configuration found:', configError);
        throw new Error('Twilio is not configured');
      }

      // Generate 6-digit OTP
      const otp = Math.floor(100000 + Math.random() * 900000).toString();

      // Store OTP in database with expiration
      const { error: otpError } = await supabase
        .from('otp_verifications')
        .insert({
          phone,
          code: otp,
          expires_at: new Date(Date.now() + 10 * 60 * 1000).toISOString(), // 10 minutes expiry
          verified: false
        });

      if (otpError) throw otpError;

      // Send SMS using Twilio
      const response = await fetch('/api/send-sms', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to: phone,
          message: `Your verification code is: ${otp}`,
          accountSid: config.configuration.account_sid,
          authToken: config.api_key,
          phoneNumber: config.configuration.phone_number
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send SMS');
      }

      terminal.log('✅ OTP sent successfully');
      return true;
    } catch (error) {
      terminal.error('❌ Error sending OTP:', error);
      set({ error: friendlyDbError(error) });
      return false;
    } finally {
      set({ loading: false });
    }
  },

  verifyOtp: async (phone: string, code: string) => {
    set({ loading: true, error: null });
    try {
      terminal.log('🔍 Verifying OTP for:', phone);

      // Get OTP record
      const { data: otpData, error: otpError } = await supabase
        .from('otp_verifications')
        .select('*')
        .eq('phone', phone)
        .eq('code', code)
        .eq('verified', false)
        .gte('expires_at', new Date().toISOString())
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (otpError) {
        terminal.error('❌ Invalid or expired OTP');
        throw new Error('Invalid or expired verification code');
      }

      // Mark OTP as verified
      const { error: updateError } = await supabase
        .from('otp_verifications')
        .update({ verified: true })
        .eq('id', otpData.id);

      if (updateError) throw updateError;

      terminal.log('✅ OTP verified successfully');
      return true;
    } catch (error) {
      terminal.error('❌ Error verifying OTP:', error);
      set({ error: friendlyDbError(error) });
      return false;
    } finally {
      set({ loading: false });
    }
  }
}));